import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Menu, X, Mail, AlertTriangle, LayoutDashboard, Users, Trophy, MessageSquare, Star, Calendar, MapPin, GraduationCap, ImageIcon, FileText, Tag, LogOut, Settings, Building, ClipboardList, UserCheck, BarChart3, ScrollText, ArrowLeft, KeyRound, Wallet, Layers } from "lucide-react";
import GlobalSearch from "@/admin/GlobalSearch";
import { api } from "@/lib/auth";
import ChangeMyPassword from "@/admin/ChangeMyPassword";
import { ConfirmProvider } from "@/lib/confirm";

function BrandMark({ className = "h-8" }) {
  const [logo, setLogo] = useState(() => sessionStorage.getItem("brand_logo") || "");
  useEffect(() => {
    api.get("/settings").then(r => { const l = r.data?.logo_light || ""; setLogo(l); sessionStorage.setItem("brand_logo", l); }).catch(() => {});
  }, []);
  return logo
    ? <img src={logo} alt="" className={`${className} w-auto object-contain`} data-testid="admin-brand-logo" />
    : <div className={`${className} aspect-square bg-red-600 grid place-items-center font-display text-xs`}>2R</div>;
}

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { section: "Content" },
  { to: "/admin/homepage", label: "Homepage", icon: LayoutDashboard },
  { to: "/admin/section-order", label: "Section Order", icon: Layers },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/pages", label: "Legal Pages", icon: ScrollText },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/promo-codes", label: "Promo Codes", icon: Tag },
  { to: "/admin/events", label: "Events", icon: Tag },
  { section: "Classes" },
  { to: "/admin/class-setup", label: "Class Setup", icon: GraduationCap, end: true },
  { to: "/admin/bulk-assign", label: "Bulk Assign", icon: Users, end: true },
  { to: "/admin/my-classes", label: "My Classes", icon: ClipboardList, end: true },
  { section: "People" },
  { to: "/admin/crews", label: "Competition Crews", icon: Trophy },
  { to: "/admin/achievements", label: "Accolades", icon: Trophy },
  { section: "Registrations" },
  { to: "/admin/all-registrations", label: "All Registrations", icon: UserCheck, end: true },
  { to: "/admin/registrations", label: "Class Registrations", icon: UserCheck },
  { to: "/admin/workshop-registrations", label: "Workshop Regs", icon: UserCheck },
  { to: "/admin/pre-registrations", label: "Pre-Registrations", icon: UserCheck },
  { to: "/admin/free-trial-registrations", label: "Free Trials", icon: UserCheck },
  { to: "/admin/competition-applications", label: "Comp. Applications", icon: UserCheck },
  { section: "Marketing" },
  { to: "/admin/workshops", label: "Workshops", icon: Building },
  { to: "/admin/google-reviews", label: "Google Reviews", icon: Star },
  { to: "/admin/reels", label: "Instagram Reels", icon: ImageIcon },
  { section: "Finance" },
  { to: "/admin/payments", label: "Payments", icon: UserCheck },
  { to: "/admin/payroll", label: "Instructor Payroll", icon: Wallet },
  { section: "Communication" },
  { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
  { to: "/admin/hire-options", label: "Hire Options", icon: Building },
  { to: "/admin/hire-inquiries", label: "Hire Inquiries", icon: MessageSquare },
  { section: "System" },
  { to: "/admin/admin-users", label: "Admins & Team", icon: UserCheck },
  { to: "/admin/email-settings", label: "Email Settings", icon: Mail },
  { to: "/admin/email-log", label: "Email Log", icon: ScrollText },
  { to: "/admin/referrals", label: "Referrals", icon: Users },
  { to: "/admin/availability", label: "Availability", icon: ClipboardList },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [changePw, setChangePw] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { if (!loading && !user) nav("/admin/login"); }, [loading, user, nav]);
  // Idle auto-logout: 30 minutes without interaction ends the admin session (shared devices).
  useEffect(() => {
    if (!user) return;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => { logout(); nav("/admin/login"); }, 30 * 60 * 1000); };
    const evs = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evs.forEach(e => window.addEventListener(e, reset, { passive: true })); reset();
    return () => { clearTimeout(timer); evs.forEach(e => window.removeEventListener(e, reset)); };
  }, [user, logout, nav]);
  useEffect(() => {
    // Force instructors onto their allowed pages — they can't view anything else.
    const p = location.pathname;
    const allowedForInstructor = p.startsWith("/admin/registrations") || p.startsWith("/admin/all-registrations") || p.startsWith("/admin/availability") || p.startsWith("/admin/my-classes") || p.startsWith("/admin/payroll");
    if (!loading && user?.role === "instructor" && !allowedForInstructor) {
      nav("/admin/my-classes", { replace: true });
    }
  }, [loading, user, nav, location.pathname]);
  if (loading || !user) return <div className="min-h-screen bg-[#050505] grid place-items-center text-zinc-500">Loading…</div>;

  const isInstructor = user.role === "instructor";
  // Instructor sidebar: My Classes + All Registrations + Availability. Full sidebar for manager/super_admin.
  const VISIBLE_NAV = isInstructor
    ? [
        { to: "/admin/my-classes", label: "My Classes", icon: GraduationCap, end: true },
        { to: "/admin/all-registrations", label: "All Registrations", icon: UserCheck, end: true },
        { to: "/admin/registrations", label: "Class Registrations", icon: UserCheck, end: true },
        { to: "/admin/payroll", label: "My Payroll", icon: Wallet, end: true },
        { to: "/admin/availability", label: "My Availability", icon: ClipboardList, end: true },
      ]
    : NAV;

  return (
    <ConfirmProvider>
    <div className="min-h-screen bg-[#050505] text-white flex flex-col lg:flex-row">
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black">
        <Link to={isInstructor ? "/admin/my-classes" : "/admin"} className="flex items-center gap-2"><BrandMark className="h-7" /><span className="font-display tracking-wider text-sm">{isInstructor ? "INSTRUCTOR" : "ADMIN"}</span></Link>
        <button onClick={() => setMenuOpen(o => !o)} className="p-2 -mr-2" aria-label="Menu" data-testid="admin-mobile-menu">{menuOpen ? <X size={20}/> : <Menu size={20}/>}</button>
      </div>
      {menuOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setMenuOpen(false)} />}
      <aside className={`w-64 border-r border-white/10 bg-black flex flex-col overflow-y-auto fixed inset-y-0 left-0 z-50 transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`} onClick={(e) => { if (e.target.closest("a")) setMenuOpen(false); }}>
        <Link to={isInstructor ? "/admin/my-classes" : "/admin"} className="flex items-center gap-2 p-6 border-b border-white/10">
          <BrandMark className="h-8" />
          <div className="font-display tracking-wider">{isInstructor ? "INSTRUCTOR" : "ADMIN"}</div>
        </Link>
        {!isInstructor && <div className="px-6 pt-4 pb-2"><GlobalSearch/></div>}
        <Link to="/" className="mx-6 mb-2 flex items-center gap-2 text-xs text-zinc-500 hover:text-red-500 uppercase tracking-widest" data-testid="nav-public-site">
          <ArrowLeft size={12}/>Back to public site
        </Link>
        <nav className="flex-1 py-4 text-sm">
          {VISIBLE_NAV.map((n, i) => n.section ? (
            <div key={`section-${n.section}`} className="px-6 pt-4 pb-2 eyebrow text-[10px]">{n.section}</div>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.end} className={({isActive}) =>
              `flex items-center gap-3 px-6 py-2 hover:bg-white/5 ${isActive ? "bg-red-600/10 text-red-500 border-l-2 border-red-500" : "text-zinc-400"}`}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s|\./g,'-')}`}>
              <n.icon size={16}/>{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-zinc-500 mb-2">{user.email}</div>
          <button onClick={() => setChangePw(true)} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-2" data-testid="change-my-password"><KeyRound size={14}/>My account</button>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500" data-testid="admin-logout"><LogOut size={14}/>Log out</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
      {/* Non-dismissible force-setup modal (until recovery_email is saved). */}
      {user && !user.recovery_email && <ChangeMyPassword onClose={() => {}} forceSetup={true}/>}
      {changePw && <ChangeMyPassword onClose={() => setChangePw(false)}/>}
    </div>
    </ConfirmProvider>
  );
}
